import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
} from 'recharts';
import type { ContactAnalyticsResponse } from '@signalvote/shared';
import { Card } from '../ui/card';
import { Skeleton } from '../ui/skeleton';

interface ContactOutcomeChartProps {
  contactsByStatus: ContactAnalyticsResponse['contactsByStatus'];
  isLoading?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  Reached: '#22c55e',
  NotHome: '#f59e0b',
  Refused: '#ef4444',
  NeedsFollowUp: '#3b82f6',
};

const STATUS_LABELS: Record<string, string> = {
  Reached: 'Reached',
  NotHome: 'Not Home',
  Refused: 'Refused',
  NeedsFollowUp: 'Needs Follow Up',
};

function formatStatusLabel(status: string): string {
  return STATUS_LABELS[status] ?? status;
}

export function ContactOutcomeChart({ contactsByStatus, isLoading }: ContactOutcomeChartProps) {
  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="space-y-4">
          <Skeleton className="h-5 w-40" />
          <Skeleton className="h-48" />
        </div>
      </Card>
    );
  }

  const chartData = contactsByStatus.map((item) => ({
    ...item,
    label: formatStatusLabel(item.status),
  }));

  return (
    <Card className="p-6">
      <h3 className="mb-4 text-lg font-semibold">Contact Outcomes</h3>

      {chartData.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">No contact data</p>
      ) : (
        <>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                  {chartData.map((entry) => (
                    <Cell
                      key={entry.status}
                      fill={STATUS_COLORS[entry.status] ?? '#6b7280'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-4 flex flex-wrap gap-4">
            {chartData.map((item) => (
              <div key={item.status} className="flex items-center gap-2 text-sm">
                <div
                  className="h-3 w-3 rounded-full"
                  style={{ backgroundColor: STATUS_COLORS[item.status] ?? '#6b7280' }}
                />
                <span className="text-muted-foreground">{item.label}</span>
                <span className="font-medium tabular-nums">{item.count}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );
}
